import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, ShieldCheck, Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SIDES = [
  { key: "front", label: "Ön tərəf" },
  { key: "back", label: "Arxa tərəf" },
  { key: "left", label: "Sol yan" },
  { key: "right", label: "Sağ yan" },
  { key: "bottom", label: "Aşağı / Şarj portu" },
  { key: "top", label: "Yuxarı / Kənar" },
];

const CONDITIONS = [
  { key: "like_new", label: "Əla", desc: "Cızıqsız, qüsursuz görünüş" },
  { key: "good", label: "Yaxşı", desc: "Yüngül istifadə izləri" },
  { key: "fair", label: "Normal", desc: "Görünən cızıqlar, kiçik qüsurlar" },
  { key: "poor", label: "Zədəli", desc: "Ciddi zədə və ya çat var" },
];

const COLORS = ["Qara", "Ağ", "Gümüşü", "Qızılı", "Mavi", "Yaşıl", "Bənövşəyi", "Qırmızı", "Çəhrayı", "Titanium"];
const STORAGE_LABEL = (gb) => (gb >= 1024 ? `${gb / 1024}TB` : `${gb}GB`);
const STEP_TITLES = ["Model", "Konfiqurasiya", "Şəkillər", "Vəziyyət & Aksesuar", "Əlaqə"];

const emptyForm = {
  model_id: "", model_name: "", storage: null, sim_type: null, condition: "",
  screen_replaced: null, battery_replaced: null, back_replaced: null, repaired_dropped: null,
  faceid_working: null, color: "", scuffs: null, chips: null, screen_scratches: null, battery_health: "",
  registered: null, beyanname: null, wifi_working: null,
  device_type: "phone", connectivity: null, case_size: null, watch_material: null,
  band_included: null, has_adapter: null, sensor_working: null, activation_lock: null,
  has_box: null, has_cable: null, photos: {}, customer_name: "", customer_phone: "", note: "",
};

const OPT_ACTIVE = "border-white/70 bg-white/[0.06] text-white";
const OPT_IDLE = "border-white/10 text-neutral-300 hover:border-white/30";

export const Wizard = ({ initial }) => {
  const [step, setStep] = useState(initial?.model_id ? 1 : 0);
  const [catalog, setCatalog] = useState([]);
  const [cat, setCat] = useState("all");
  const [form, setForm] = useState({ ...emptyForm, ...(initial || {}) });
  const [estimate, setEstimate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const selectedModel = useMemo(() => catalog.find((m) => m.model_id === form.model_id), [catalog, form.model_id]);
  const categories = useMemo(() => ["all", ...Array.from(new Set(catalog.map((m) => m.category)))], [catalog]);
  const shown = catalog.filter((m) => cat === "all" || m.category === cat);

  useEffect(() => {
    if (selectedModel && form.storage != null && !selectedModel.storages.includes(form.storage)) {
      setForm((f) => ({ ...f, storage: null }));
    }
  }, [selectedModel]); // eslint-disable-line

  useEffect(() => {
    if (!form.model_id || !form.condition || (selectedModel?.device_type !== "watch" && form.storage == null)) {
      setEstimate(null);
      return;
    }
    const t = setTimeout(() => {
      api.post("/estimate", form).then((r) => setEstimate(r.data.estimate ? r.data : null)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [form]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const pickModel = (m) => set({ model_id: m.model_id, model_name: m.name, storage: null, color: "", sim_type: m.has_esim === false ? "dual" : null, device_type: m.device_type || "phone", connectivity: null, case_size: null, watch_material: null });

  const setPhoto = (key, val) => setForm((f) => {
    const photos = { ...f.photos };
    if (val) photos[key] = val; else delete photos[key];
    return { ...f, photos };
  });

  const canNext = () => {
    if (step === 0) return !!form.model_id;
    const dev = selectedModel?.device_type || "phone";
    if (step === 1) {
      if (dev === "watch") return !!form.case_size && !!form.connectivity && !!form.watch_material && !!form.condition;
      if (dev === "tablet") return form.storage != null && !!form.connectivity && !!form.condition;
      return form.storage != null && !!form.sim_type && !!form.color && !!form.condition;
    }
    if (step === 2) return true;
    if (step === 3) {
      const setb = (v) => v === true || v === false;
      const bh = Number(form.battery_health);
      const batteryOk = form.battery_health !== "" && form.battery_health != null && !Number.isNaN(bh) && bh >= 1 && bh <= 100;
      if (dev === "phone") {
        const defects = ["screen_replaced", "battery_replaced", "back_replaced", "repaired_dropped", "scuffs", "chips", "screen_scratches"];
        const defectsOk = defects.every((k) => setb(form[k]));
        const faceidOk = !selectedModel?.has_faceid || setb(form.faceid_working);
        const accOk = setb(form.has_box) && setb(form.has_cable);
        const statusOk = [form.registered, form.beyanname, form.wifi_working].every(setb);
        return defectsOk && faceidOk && batteryOk && accOk && statusOk;
      }
      const defects = dev === "watch" ? ["scuffs", "chips", "screen_scratches"] : ["screen_replaced", "repaired_dropped", "scuffs", "chips", "screen_scratches"];
      const defectsOk = defects.every((k) => setb(form[k]));
      const devOk = setb(form.activation_lock) && setb(form.has_adapter) && (dev !== "watch" || (setb(form.sensor_working) && setb(form.band_included)));
      const regOk = dev !== "tablet" || form.connectivity !== "cellular" || [form.registered, form.beyanname].every(setb);
      return defectsOk && batteryOk && devOk && regOk && form.activation_lock !== true;
    }
    if (step === 4) return form.customer_name.trim().length > 1 && form.customer_phone.trim().length >= 7;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        battery_health: form.battery_health ? parseInt(form.battery_health) : null,
        photos: Object.entries(form.photos).map(([side, v]) => ({ side, path: v.path, url: v.url })),
      };
      await api.post("/submissions", payload);
      setDone(true);
    } catch (e) {
      toast.error("Müraciət göndərilmədi. Yenidən cəhd edin.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setForm(emptyForm); setEstimate(null); setStep(0); setDone(false); };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl border border-white/10 bg-[#111] p-10 text-center max-w-xl mx-auto" data-testid="submission-success">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={1.4} />
        </div>
        <h3 className="font-serif text-2xl mb-2">Müraciətiniz göndərildi</h3>
        <p className="text-neutral-400 mb-1">Təşəkkür edirik, {form.customer_name}.</p>
        <p className="text-neutral-400 mb-6">Ekspertlərimiz qiyməti dəqiqləşdirmək üçün tezliklə sizinlə əlaqə saxlayacaq.</p>
        {estimate && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-200 mb-6">
            Təxmini dəyər: {estimate.estimate.toLocaleString("ru-RU")} ₼
          </div>
        )}
        <div>
          <button onClick={reset} data-testid="new-submission-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-neutral-100 text-black font-medium hover:bg-white transition-colors">
            <RotateCcw className="w-4 h-4" /> Yeni müraciət
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div data-testid="wizard">
      {/* stepper */}
      <div className="flex items-center justify-center gap-1.5 sm:gap-3 mb-8 flex-wrap">
        {STEP_TITLES.map((t, i) => (
          <div key={t} className="flex items-center gap-1.5 sm:gap-3">
            <div className={`flex items-center gap-2 text-xs sm:text-sm transition-colors ${
              i === step ? "text-white" : i < step ? "text-neutral-300" : "text-neutral-600"
            }`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] border ${
                i < step ? "border-white/40 bg-white/10" : i === step ? "border-white bg-white text-black" : "border-white/15"
              }`}>
                {i < step ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              <span className="hidden sm:inline">{t}</span>
            </div>
            {i < STEP_TITLES.length - 1 && <div className="w-4 sm:w-8 h-px bg-white/10" />}
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#111]/70 p-5 sm:p-8 min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>

            {/* STEP 0 — MODEL */}
            {step === 0 && (
              <div>
                <h3 className="font-serif text-2xl mb-1">iPhone modelini seçin</h3>
                <p className="text-neutral-400 text-sm mb-5">Satmaq istədiyiniz modeli seçin.</p>
                <div className="flex gap-2 flex-wrap mb-5">
                  {categories.map((c) => (
                    <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        cat === c ? "bg-neutral-100 text-black" : "bg-white/5 text-neutral-400 hover:text-white"
                      }`}>
                      {c === "all" ? "Hamısı" : c}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {shown.map((m) => {
                    const active = form.model_id === m.model_id;
                    return (
                      <button key={m.model_id} onClick={() => pickModel(m)} data-testid={`select-model-${m.model_id}`}
                        className={`group relative rounded-2xl p-4 text-left border transition-colors ${active ? OPT_ACTIVE : "border-white/8 bg-white/[0.02] hover:border-white/25"}`}>
                        {m.image_url ? (
                          <img src={fileUrl(m.image_url)} alt={m.name} className="w-14 h-14 mx-auto mb-3 object-contain" />
                        ) : (
                          <div className="w-8 h-12 mx-auto mb-3 rounded-md border border-white/20 bg-gradient-to-b from-white/10 to-transparent relative">
                            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-0.5 rounded-full bg-white/30" />
                          </div>
                        )}
                        <div className="text-sm font-medium leading-tight">{m.name}</div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">{m.base_price}+ ₼</div>
                        {active && <Check className="w-4 h-4 text-white absolute top-3 right-3" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 1 — CONFIG */}
            {step === 1 && selectedModel && (
              <div className="space-y-7">
                <div>
                  <h3 className="font-serif text-2xl mb-1">{selectedModel.name}</h3>
                  <p className="text-neutral-400 text-sm">Yaddaş, SIM növü və vəziyyəti seçin.</p>
                </div>
                {selectedModel.device_type !== "watch" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Yaddaş</label>
                  <div className="flex gap-2.5 flex-wrap mt-3">
                    {selectedModel.storages.map((s) => (
                      <button key={s} onClick={() => set({ storage: s })} data-testid={`select-storage-${s}`}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.storage === s ? OPT_ACTIVE : OPT_IDLE}`}>
                        {STORAGE_LABEL(s)}
                      </button>
                    ))}
                  </div>
                </div>
                )}
                {selectedModel.device_type === "watch" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Korpus ölçüsü</label>
                  <div className="flex gap-2.5 flex-wrap mt-3">
                    {["40 mm", "41 mm", "42 mm", "44 mm", "45 mm", "46 mm", "49 mm"].map((s) => (
                      <button key={s} onClick={() => set({ case_size: s })} data-testid={`select-case-${s}`}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.case_size === s ? OPT_ACTIVE : OPT_IDLE}`}>{s}</button>
                    ))}
                  </div>
                </div>
                )}
                {(selectedModel.device_type === "tablet" || selectedModel.device_type === "watch") && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{selectedModel.device_type === "watch" ? "Versiya" : "Bağlantı"}</label>
                  <div className="flex gap-2.5 flex-wrap mt-3">
                    {(selectedModel.device_type === "watch" ? [{ k: "gps", l: "GPS" }, { k: "cellular", l: "GPS + Cellular" }] : [{ k: "wifi", l: "Wi‑Fi" }, { k: "cellular", l: "Wi‑Fi + Cellular" }]).map((o) => (
                      <button key={o.k} onClick={() => set({ connectivity: o.k })} data-testid={`select-conn-${o.k}`}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.connectivity === o.k ? OPT_ACTIVE : OPT_IDLE}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                )}
                {selectedModel.device_type === "watch" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Material</label>
                  <div className="flex gap-2.5 flex-wrap mt-3">
                    {["Aluminium", "Stainless Steel", "Titanium"].map((mt) => (
                      <button key={mt} onClick={() => set({ watch_material: mt })} data-testid={`select-material-${mt}`}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.watch_material === mt ? OPT_ACTIVE : OPT_IDLE}`}>{mt}</button>
                    ))}
                  </div>
                </div>
                )}
                {selectedModel.has_esim !== false && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">SIM Növü</label>
                  <div className="flex gap-2.5 flex-wrap mt-3">
                    {[{ k: "dual", l: "Fiziki SIM + eSIM" }, { k: "dualsim", l: "Dual SIM + eSIM" }, { k: "esim", l: "Yalnız eSIM" }].map((o) => (
                      <button key={o.k} onClick={() => set({ sim_type: o.k })} data-testid={`select-sim-${o.k}`}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.sim_type === o.k ? OPT_ACTIVE : OPT_IDLE}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Rəng</label>
                  <div className="flex gap-2.5 flex-wrap mt-3">
                    {(selectedModel.colors?.length ? selectedModel.colors : COLORS).map((c) => (
                      <button key={c} onClick={() => set({ color: c })} data-testid={`select-color-${c}`}
                        className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.color === c ? OPT_ACTIVE : OPT_IDLE}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Vəziyyət</label>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    {CONDITIONS.map((c) => (
                      <button key={c.key} onClick={() => set({ condition: c.key })} data-testid={`select-condition-${c.key}`}
                        className={`text-left rounded-2xl p-4 border transition-colors ${form.condition === c.key ? OPT_ACTIVE : "border-white/8 bg-white/[0.02] hover:border-white/25"}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{c.label}</span>
                          {form.condition === c.key && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <p className="text-xs text-neutral-500 mt-1">{c.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2 — PHOTOS */}
            {step === 2 && (
              <div>
                <h3 className="font-serif text-2xl mb-1">Telefonun şəkillərini yükləyin</h3>
                <p className="text-neutral-400 text-sm mb-5">Hər tərəfi ayrıca çəkin — cızıq və çatlar aydın görünsün.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {SIDES.map((s) => (
                    <PhotoUpload key={s.key} side={s} value={form.photos[s.key]} onChange={setPhoto} />
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3 — HISTORY & ACCESSORIES */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl mb-1">Telefonun tarixçəsi</h3>
                  <p className="text-neutral-400 text-sm">Dəqiq qiymət üçün doğru məlumat verin.</p>
                </div>
                <div className="space-y-3">
                  {selectedModel?.has_faceid && (
                    <div className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3" data-testid="faceid-row">
                      <span className="text-sm">Face ID işləyir?</span>
                      <div className="flex gap-2">
                        <button onClick={() => set({ faceid_working: false })} data-testid="toggle-faceid_working-no"
                          className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form.faceid_working === false ? OPT_ACTIVE : OPT_IDLE}`}>
                          Xeyr
                        </button>
                        <button onClick={() => set({ faceid_working: true })} data-testid="toggle-faceid_working"
                          className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form.faceid_working === true ? OPT_ACTIVE : OPT_IDLE}`}>
                          Bəli
                        </button>
                      </div>
                    </div>
                  )}
                  {(selectedModel?.device_type === "watch"
                    ? [{ k: "scuffs", l: "Korpusda cızıq var?" }, { k: "chips", l: "Korpusda əzik var?" }, { k: "screen_scratches", l: "Ekranda cızıqlar var?" }]
                    : selectedModel?.device_type === "tablet"
                    ? [{ k: "screen_replaced", l: "Ekran dəyişdirilib?" }, { k: "repaired_dropped", l: "Təmirdə olub?" }, { k: "scuffs", l: "Korpusda cızıq / sürtülmə var?" }, { k: "chips", l: "Korpusda əzik / sıyrıq var?" }, { k: "screen_scratches", l: "Ekranda cızıqlar var?" }]
                    : [
                    { k: "screen_replaced", l: "Ekran dəyişdirilib?" },
                    { k: "battery_replaced", l: "Batareya dəyişdirilib?" },
                    { k: "back_replaced", l: "Arxa qapaq dəyişdirilib?" },
                    { k: "repaired_dropped", l: "Təmirdə olub?" },
                    { k: "scuffs", l: "Korpusda cızıq / sürtülmə var?" },
                    { k: "chips", l: "Korpusda əzik / sıyrıq var?" },
                    { k: "screen_scratches", l: "Ekranda cızıqlar var?" },
                  ]).map((r) => (
                    <div key={r.k} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                      <span className="text-sm">{r.l}</span>
                      <div className="flex gap-2">
                        <button onClick={() => set({ [r.k]: false })} data-testid={`toggle-${r.k}-no`}
                          className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[r.k] === false ? OPT_ACTIVE : OPT_IDLE}`}>
                          Xeyr
                        </button>
                        <button onClick={() => set({ [r.k]: true })} data-testid={`toggle-${r.k}`}
                          className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[r.k] === true ? OPT_ACTIVE : OPT_IDLE}`}>
                          Bəli
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {(selectedModel?.device_type === "phone" || (selectedModel?.device_type === "tablet" && form.connectivity === "cellular")) && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Sənədlər və şəbəkə</label>
                  <div className="space-y-3 mt-3">
                    {(selectedModel?.device_type === "phone"
                      ? [{ k: "registered", l: "Şəbəkə qeydiyyatı var?" }, { k: "beyanname", l: "Bəyannamə var?" }, { k: "wifi_working", l: "Wi‑Fi işləyir?" }]
                      : [{ k: "registered", l: "Şəbəkə qeydiyyatı var?" }, { k: "beyanname", l: "Bəyannamə var?" }]
                    ).map((r) => (
                      <div key={r.k} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                        <span className="text-sm">{r.l}</span>
                        <div className="flex gap-2">
                          <button onClick={() => set({ [r.k]: false })} data-testid={`toggle-${r.k}-no`}
                            className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[r.k] === false ? OPT_ACTIVE : OPT_IDLE}`}>
                            Xeyr
                          </button>
                          <button onClick={() => set({ [r.k]: true })} data-testid={`toggle-${r.k}`}
                            className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[r.k] === true ? OPT_ACTIVE : OPT_IDLE}`}>
                            Bəli
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Batareya sağlamlığı</label>
                  <p className="text-xs text-neutral-500 mt-1 mb-3">Ayarlar → Batareya bölməsindəki faizi yazın (mütləq).</p>
                  <div className="relative max-w-[180px]">
                    <Input type="number" min="0" max="100" value={form.battery_health}
                      onChange={(e) => set({ battery_health: e.target.value })}
                      placeholder="məs. 92" data-testid="input-battery-health"
                      className="bg-white/5 border-white/10 h-12 pr-9" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">%</span>
                  </div>
                </div>
                {(selectedModel?.device_type === "tablet" || selectedModel?.device_type === "watch") && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Cihaz vəziyyəti</label>
                  <div className="space-y-3 mt-3">
                    {[
                      { k: "activation_lock", l: "Activation Lock (iCloud kilidi) var?" },
                      { k: "has_adapter", l: "Orijinal adapter var?" },
                      ...(selectedModel?.device_type === "watch" ? [{ k: "sensor_working", l: "Sensor işləyir?" }, { k: "band_included", l: "Orijinal qayış var?" }] : []),
                    ].map((r) => (
                      <div key={r.k} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                        <span className="text-sm">{r.l}</span>
                        <div className="flex gap-2">
                          <button onClick={() => set({ [r.k]: false })} data-testid={`toggle-${r.k}-no`}
                            className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[r.k] === false ? OPT_ACTIVE : OPT_IDLE}`}>Xeyr</button>
                          <button onClick={() => set({ [r.k]: true })} data-testid={`toggle-${r.k}`}
                            className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[r.k] === true ? OPT_ACTIVE : OPT_IDLE}`}>Bəli</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {form.activation_lock === true && <p className="text-xs text-red-400 mt-2">Activation Lock ilə cihaz qəbul edilmir.</p>}
                </div>
                )}
                {selectedModel?.device_type !== "watch" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Aksesuarlar</label>
                  <div className="space-y-3 mt-3">
                    {[{ k: "has_box", l: "Orijinal qutu var?" }, { k: "has_cable", l: "Orijinal kabel var?" }].map((a) => (
                      <div key={a.k} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                        <span className="text-sm">{a.l}</span>
                        <div className="flex gap-2">
                          <button onClick={() => set({ [a.k]: false })} data-testid={`toggle-${a.k}-no`}
                            className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[a.k] === false ? OPT_ACTIVE : OPT_IDLE}`}>
                            Xeyr
                          </button>
                          <button onClick={() => set({ [a.k]: true })} data-testid={`toggle-${a.k}`}
                            className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${form[a.k] === true ? OPT_ACTIVE : OPT_IDLE}`}>
                            Bəli
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>
            )}

            {/* STEP 4 — CONTACT */}
            {step === 4 && (
              <div className="space-y-5 max-w-lg">
                <div>
                  <h3 className="font-serif text-2xl mb-1">Əlaqə məlumatları</h3>
                  <p className="text-neutral-400 text-sm">Sizinlə əlaqə saxlaya bilməyimiz üçün.</p>
                </div>
                <div>
                  <label className="text-sm text-neutral-300 mb-1.5 block">Ad Soyad</label>
                  <Input value={form.customer_name} onChange={(e) => set({ customer_name: e.target.value })}
                    placeholder="Adınız" data-testid="input-name" className="bg-white/5 border-white/10 h-12" />
                </div>
                <div>
                  <label className="text-sm text-neutral-300 mb-1.5 block">Mobil nömrə</label>
                  <Input value={form.customer_phone} onChange={(e) => set({ customer_phone: e.target.value })}
                    placeholder="+994 50 123 45 67" data-testid="input-phone" className="bg-white/5 border-white/10 h-12" />
                </div>
                <div>
                  <label className="text-sm text-neutral-300 mb-1.5 block">Əlavə qeyd (istəyə görə)</label>
                  <Textarea value={form.note} onChange={(e) => set({ note: e.target.value })}
                    placeholder="Telefon haqqında əlavə məlumat..." data-testid="input-note" className="bg-white/5 border-white/10" />
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* footer: estimate + nav */}
      <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#111]/70 px-5 py-3" data-testid="price-bar">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">{step < 4 ? "Təxmini qiymət" : "Yekun təxmini qiymət"}</div>
          {estimate ? (
            <div className="font-display font-medium text-2xl text-white" data-testid="estimate-value">
              {step < 4
                ? `${estimate.low.toLocaleString("ru-RU")}–${estimate.high.toLocaleString("ru-RU")}`
                : estimate.estimate.toLocaleString("ru-RU")} <span className="text-neutral-500">₼</span>
            </div>
          ) : (
            <div className="text-sm text-neutral-500">Model və vəziyyəti seçin</div>
          )}
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)} data-testid="wizard-back"
              className="flex items-center gap-1.5 px-5 py-3 rounded-xl border border-white/15 text-neutral-300 hover:bg-white/5 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Geri
            </button>
          )}
          {step < 4 ? (
            <button onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()} data-testid="wizard-next"
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-7 py-3 rounded-xl bg-neutral-100 text-black font-medium disabled:opacity-40 hover:bg-white transition-colors">
              Növbəti <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={!canNext() || submitting} data-testid="wizard-submit"
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-7 py-3 rounded-xl bg-neutral-100 text-black font-medium disabled:opacity-40 hover:bg-white transition-colors">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Müraciəti göndər
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
